import BaseStore from "../base";
import { action } from "mobx";
import http from "@app/lib/http";
import notify from "@app/components/notify";
import ImageModel from "@app/state/model/report-document/image";
import reportDocuments from "@app/state/store/report-document/report-documents";

export class ImageStore extends BaseStore {
    observable() {
        return {
            loading: false,
        };
    }

    @action
    async format(file, { clientId }) {
        if (!file) {
            console.log("no file or projectId");

            return;
        }

        this.loading = true;
        let formData = new FormData();
        formData.append("file", file);

        if (clientId) {
            formData.append("clientId", clientId);
        }

        try {
            let { data } = await http.post(`/image/upload`, formData);
            if (data) {
                return new ImageModel(data);
            }
        } catch (error) {
            notify.error("Cannot format file!");
        } finally {
            this.loading = false;
        }
    }

    @action
    async formatImage({ type, value, caption, clientId, width, height }) {
        const convertFileToImage = async (file) => {
            // Check if the file is actually an image
            if (!file || !file.type) {
                return null;
            }

            // Validate it's an image file
            if (!file.type.startsWith("image/")) {
                return null;
            }

            const image = await this.format(file, { clientId });
            if (image) {
                return {
                    file: image.file,
                    processed: true,
                    height: height ?? image.height,
                    width: width ?? image.width,
                    caption: caption,
                    src: null,
                };
            }
        };

        const convertLinkToImage = async (link) => {
            // fetch the image
            try {
                const response = await fetch(link);
                const blob = await response.blob();

                const image = await this.format(blob, { clientId });

                if (image) {
                    return {
                        file: image.file,
                        processed: true,
                        height: height ?? image.height,
                        width: width ?? image.width,
                        caption: caption,
                        src: null,
                    };
                }
            } catch (error) {
                // TODO: future improvement: in case of CORS error fetch the image via backend and return the file
                notify.error(
                    "Unable to fetch this image due to policy on the source website. Try downloading it first and then uploading it instead.",
                );
            }
        };

        const convertBase64ToImage = async (value) => {
            const src = value;

            if (src) {
                const data = src.split(",");
                if (data[0].match(/data:image.+base64/)) {
                    try {
                        // Validate the base64 data is actually an image
                        const imageType = data[0].match(/data:image\/([^;]+)/);
                        if (!imageType) {
                            return null;
                        }

                        var binary = window.atob(data[1]);
                        var array = [];
                        for (var i = 0; i < binary.length; i++) {
                            array.push(binary.charCodeAt(i));
                        }

                        // Create blob with the correct image type
                        var file = new Blob([new Uint8Array(array)], {
                            type: `image/${imageType[1]}`,
                        });

                        return await convertFileToImage(file);
                    } catch (error) {
                        console.error("Error processing base64 image:", error);
                        return null;
                    }
                }
            }
        };

        const getActualType = (type, value) => {
            if (type === "file") {
                return "file";
            }

            if (value.includes("http")) {
                return "link";
            }

            if (value.includes("data:image")) {
                return "base64";
            }

            return null;
        };

        const actualType = getActualType(type, value);

        switch (actualType) {
            case "file":
                return await convertFileToImage(value);
            case "link":
                return await convertLinkToImage(value);
            case "base64":
                return await convertBase64ToImage(value);
            default:
                return null;
        }
    }

    /**
     * Get a unique list of image guids that have been used within the report document
     */
    get images() {
        return reportDocuments.figureSet;
    }
}

export default new ImageStore();
